import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import Modal, { Field, useModalClose } from './Modal';
import { Chip } from './StatusChip';
import DataTable from './DataTable';
import EmptyState from './EmptyState';
import { useConfirm } from './ConfirmDialog';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import { formatDate, formatGBP, todayLocalISO } from '../utils/format';

/**
 * <ClientPoPanel /> — inbound client purchase orders.
 *
 * The billing session's biggest find: CloudSafe only had the outbound PO, the one
 * you raise to buy goods and services. Harvey: "there's two types of POs — one is
 * recorded, and one is sent to you by the customer's sales." Joanne and Josh both
 * said "we need both."
 *
 * This is the inbound one — the client's authority for us to invoice. CBRE order
 * £500 of cradle testing; that order has to appear on the invoice or it doesn't get
 * paid. The reference is free text because in practice it often isn't a number:
 * "sometimes it's an email, sometimes it's a person's name that you use as the
 * purchase order reference to get paid."
 *
 * Renders inside a job (jobId set — Joanne's "can we not add the purchase order
 * from that job") or standalone on the billing screen.
 */

const REF_KINDS = [
  { id: 'po', label: 'Purchase order', hint: 'A PO document or number' },
  { id: 'email', label: 'Email instruction', hint: 'Ordered by email, no formal PO' },
  { id: 'verbal', label: 'Named authority', hint: 'Someone’s name used as the reference' },
];
// Status → tone + label (words, never colour alone).
const STATUS_CHIP = {
  open: ['info', 'Open'], invoiced: ['success', 'Invoiced'],
  closed: ['neutral', 'Closed'], cancelled: ['muted', 'Cancelled'],
};
const money = (n) => (n == null || n === '' ? '—' : formatGBP(n));
const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' };
const dash = <span style={{ color: 'var(--cw-placeholder)' }} aria-label="Not set">—</span>;
const CancelButton = ({ disabled }) => {
  const requestClose = useModalClose();
  return <button type="button" className="cw-btn cw-btn-secondary" onClick={requestClose} disabled={disabled}>Cancel</button>;
};

const EMPTY = { reference: '', ref_kind: 'po', client: '', value: '', received_on: '', job_id: '', notes: '', bill_to: '', care_of: '' };

// readOnly: list only — no add / edit / remove (e.g. a job the viewer can't change).
const ClientPoPanel = ({ jobId = null, propertyId = null, defaultClient = '', compact = false, onChanged, readOnly = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [initialForm, setInitialForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const { confirm } = useConfirm();
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [jobOptions, setJobOptions] = useState([]);
  const [parties, setParties] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = jobId ? `?job_id=${jobId}` : (propertyId ? `?property_id=${propertyId}` : '');
      const d = await apiFetch(`/api/contractor/client-pos${qs}`);
      setRows(Array.isArray(d?.client_pos) ? d.client_pos : []);
      setLoadError(null);
    } catch (e) { setLoadError(e); }
    finally { setLoading(false); }
  }, [jobId, propertyId]);
  useEffect(() => { load(); }, [load]);

  // The bill-to preview: "Legal & General Trustee One, care of Cushman & Wakefield".
  // Resolved from the property's related companies so you can see who actually gets
  // the invoice before you commit to one.
  useEffect(() => {
    if (!open) return;
    const q = jobId ? `job_id=${jobId}` : propertyId ? `property_id=${propertyId}` : '';
    if (!q) { setParties(null); return; }
    apiFetch(`/api/contractor/billing-parties?${q}`).then(setParties).catch(() => setParties(null));   // preview only
  }, [open, jobId, propertyId]);

  // Only needed standalone — inside a job the job is already known. Filtered to the
  // property and excluding finished work, which is how the meeting settled the
  // "hundreds of jobs in a dropdown" problem without adding a search box.
  useEffect(() => {
    if (!open || jobId) return;
    const qs = propertyId ? `?property_id=${propertyId}` : '';
    apiFetch(`/api/contractor/po-job-options${qs}`)
      .then((d) => setJobOptions(Array.isArray(d?.jobs) ? d.jobs : []))
      .catch(() => setJobOptions([]));
  }, [open, jobId, propertyId]);

  const openNew = () => {
    setEditId(null);
    const f = { ...EMPTY, client: defaultClient || '', job_id: jobId || '', received_on: todayLocalISO() };
    setForm(f); setInitialForm(f); setErrors({}); setFormError(null);
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditId(r.id);
    const f = {
      reference: r.reference || '', ref_kind: r.ref_kind || 'po', client: r.client || '',
      value: r.value == null ? '' : String(r.value),
      received_on: (r.received_on || '').slice(0, 10), job_id: r.job_id || '',
      notes: r.notes || '', bill_to: r.bill_to || '', care_of: r.care_of || '',
    };
    setForm(f); setInitialForm(f); setErrors({}); setFormError(null);
    setOpen(true);
  };
  const close = () => { setOpen(false); setEditId(null); setForm(EMPTY); };
  const setF = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((x) => ({ ...x, [k]: null })); };

  const save = async () => {
    const errs = {};
    if (!form.reference.trim()) errs.reference = 'Enter the reference — a PO number, an email, or who authorised it';
    if (form.value !== '' && (!Number.isFinite(Number(form.value)) || Number(form.value) < 0)) errs.value = 'Enter an amount in pounds, e.g. 500.00';
    if (form.received_on && form.received_on > todayLocalISO()) errs.received_on = 'The order can’t have been received in the future';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true); setFormError(null);
    try {
      const body = {
        ...form,
        value: form.value === '' ? null : Number(form.value),
        job_id: form.job_id ? Number(form.job_id) : null,
        property_id: propertyId || undefined,
      };
      if (editId) {
        await apiFetch(`/api/contractor/client-pos/${editId}`, { method: 'PUT', body: JSON.stringify(body) });
        toast.success(`${form.reference.trim()} updated`);
      } else {
        await apiFetch('/api/contractor/client-pos', { method: 'POST', body: JSON.stringify(body) });
        toast.success(`${form.reference.trim()} recorded`);
      }
      close(); load(); onChanged?.();
    } catch (e) { setFormError(apiErrorMessage(e, "Couldn't save the client PO")); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    const ok = await confirm({ title: `Remove client PO ${r.reference}?`, body: 'Invoices can no longer quote it.', confirmLabel: 'Remove PO', tone: 'danger' });
    if (!ok) return;
    try {
      await apiFetch(`/api/contractor/client-pos/${r.id}`, { method: 'DELETE' });
      toast.success(`${r.reference} removed`); load(); onChanged?.();
    } catch (e) {
      if (e?.status === 409) {
        const force = await confirm({
          title: 'Invoices quote this PO',
          body: `${e.body?.message || 'Invoices cite this PO.'} It forms part of the invoice audit trail — removing it leaves those invoices pointing at nothing.`,
          confirmLabel: 'Remove anyway', tone: 'danger',
        });
        if (!force) return;
        try { await apiFetch(`/api/contractor/client-pos/${r.id}?force=1`, { method: 'DELETE' }); toast.success(`${r.reference} removed`); load(); onChanged?.(); }
        catch (err) { toast.error(`Couldn't remove it: ${apiErrorMessage(err)}`); }
      } else toast.error(`Couldn't remove it: ${apiErrorMessage(e)}`);
    }
  };

  const totals = useMemo(() => rows.reduce((a, r) => ({
    authorised: a.authorised + Number(r.value || 0),
    invoiced: a.invoiced + Number(r.invoiced_total || 0),
  }), { authorised: 0, invoiced: 0 }), [rows]);

  const refKind = REF_KINDS.find((k) => k.id === form.ref_kind) || REF_KINDS[0];

  const columns = [
    { key: 'reference', label: 'Reference', primary: true, render: (r) => (
      <div>
        <div style={{ fontWeight: 700, ...mono }}>{r.reference}</div>
        <div className="cw-meta" style={{ marginTop: 2 }}>
          {(REF_KINDS.find((k) => k.id === r.ref_kind) || REF_KINDS[0]).label}
          {r.received_on ? ` · ${formatDate(r.received_on)}` : ''}
        </div>
      </div>
    ) },
    { key: 'bill_to', label: 'Bill to', sortValue: (r) => r.bill_to || r.client || '', render: (r) => (
      <div>
        <div>{r.bill_to || r.client || dash}</div>
        {r.care_of && <div className="cw-meta" style={{ marginTop: 2 }}>care of {r.care_of}</div>}
      </div>
    ) },
    ...(!compact ? [{ key: 'job_number', label: 'Job', render: (r) => (
      <div>
        {r.job_number ? <span style={mono}>{r.job_number}</span> : dash}
        {r.property_name && <div className="cw-meta">{r.property_name.trim()}</div>}
      </div>
    ) }] : []),
    { key: 'value', label: 'Authorised', align: 'right', sortValue: (r) => Number(r.value || 0), render: (r) => <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{money(r.value)}</span> },
    { key: 'invoiced_total', label: 'Invoiced', align: 'right', sortValue: (r) => Number(r.invoiced_total || 0), render: (r) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{money(r.invoiced_total)}</span> },
    { key: 'remaining', label: 'Remaining', align: 'right', sortValue: (r) => (r.remaining == null ? null : Number(r.remaining)), render: (r) => {
      const over = r.remaining != null && r.remaining < 0;
      return (
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: over ? 'var(--cw-danger-fg)' : undefined }}>
          {r.remaining == null ? '—' : money(r.remaining)}
          {over && <><br /><Chip tone="danger" label="Over the PO value" /></>}
        </span>
      );
    } },
    { key: 'status', label: 'Status', render: (r) => { const [tone, label] = STATUS_CHIP[r.status] || STATUS_CHIP.open; return <Chip tone={tone} label={label} dot />; } },
    ...(readOnly ? [] : [{ key: 'actions', label: '', sortable: false, align: 'right', render: (r) => (
      <span style={{ display: 'inline-flex', gap: 5, whiteSpace: 'nowrap' }}>
        <button type="button" className="cw-btn cw-btn-secondary" onClick={() => openEdit(r)} aria-label={`Edit ${r.reference}`}>Edit</button>
        <button type="button" className="cw-btn cw-btn-danger-outline" onClick={() => remove(r)} aria-label={`Remove ${r.reference}`}>Remove</button>
      </span>
    ) }]),
  ];

  return (
    <>
      <div className="cw-card" style={{ overflow: 'hidden' }}>
        <div className="cw-card-head">
          <h3>{compact ? 'Client POs' : 'Client purchase orders'}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {rows.length > 0 && (
              <span className="cw-meta">
                {money(totals.authorised)} authorised · {money(totals.invoiced)} invoiced
              </span>
            )}
            {!readOnly && (
              <button type="button" className="cw-btn cw-btn-primary" onClick={openNew}>
                <i className="fa-solid fa-plus" aria-hidden="true" /> Add client PO
              </button>
            )}
          </div>
        </div>

        {!compact && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 'var(--cs-text-sm)', color: 'var(--cs-blue-dark, #1565C0)', background: 'var(--cw-blue-tint)', borderBottom: '1px solid var(--cs-blue-light, #bfdbfe)', padding: '10px 18px', lineHeight: 1.5 }}>
            <i className="fa-solid fa-circle-info" style={{ marginTop: 2 }} aria-hidden="true" />
            <span>
              The client’s order <strong>to you</strong> — your authority to invoice. Not the same as a
              <strong> purchase order</strong>, which is what you send a supplier. The reference here is
              whatever the client gave you: a PO number, an email, or just the name of whoever authorised it.
            </span>
          </div>
        )}

        <DataTable
          columns={columns}
          rows={rows}
          minWidth={compact ? 520 : 760}
          loading={loading}
          error={loadError}
          onRetry={load}
          errorThing="client POs"
          getRowKey={(r) => r.id}
          getRowLabel={(r) => r.reference}
          emptyState={<EmptyState compact icon="fa-solid fa-file-signature" title={`No client PO recorded ${jobId ? 'against this job' : 'yet'}`}
            body="Without one, an invoice is likely to be rejected — record it here as soon as the order comes in." actionLabel={readOnly ? undefined : 'Add client PO'} onAction={readOnly ? undefined : openNew} />}
        />
      </div>

      {open && (
        <Modal
          title={editId ? 'Edit client PO' : 'Record a client PO'} maxWidth={560}
          dirty={JSON.stringify(form) !== JSON.stringify(initialForm)}
          onClose={() => !saving && close()}
          footer={<>
            <CancelButton disabled={saving} />
            <button type="button" className="cw-btn cw-btn-primary" onClick={save} disabled={saving} aria-busy={saving || undefined}>
              <i className={`fa-solid ${saving ? 'fa-spinner fa-spin' : 'fa-check'}`} aria-hidden="true" /> {saving ? 'Saving…' : editId ? 'Save changes' : 'Record PO'}
            </button>
          </>}
        >
          {formError && <div className="cw-error-state compact" role="alert" style={{ marginBottom: 12 }}><div className="cw-error-state-text"><p className="cw-error-state-title">Not saved</p><p className="cw-error-state-reason">{formError}</p></div></div>}
          <div style={{ display: 'grid', gap: 12 }}>
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <legend className="cw-label" style={{ marginBottom: 5 }}>How did the order arrive?</legend>
              <div role="radiogroup" aria-label="How did the order arrive?" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {REF_KINDS.map((k) => {
                  const on = form.ref_kind === k.id;
                  return (
                    <button key={k.id} type="button" role="radio" aria-checked={on}
                      onClick={() => setF('ref_kind', k.id)}
                      style={{
                        padding: '6px 12px', minHeight: 36, borderRadius: 999, fontSize: 'var(--cs-text-sm)', cursor: 'pointer',
                        fontWeight: on ? 700 : 500,
                        border: `1px solid ${on ? 'var(--cw-blue)' : 'var(--cw-border-strong)'}`,
                        background: on ? 'var(--cw-blue-tint)' : 'var(--cw-surface)',
                        color: on ? 'var(--cs-blue-dark, #1565C0)' : 'var(--cw-text-2)',
                      }}>{on && <i className="fa-solid fa-check" aria-hidden="true" style={{ marginRight: 5 }} />}{k.label}</button>
                  );
                })}
              </div>
              <p className="cw-meta" style={{ margin: '5px 0 0' }}>{refKind.hint}</p>
            </fieldset>

            <Field label="Reference" required error={errors.reference}>
              <input value={form.reference} autoFocus
                placeholder={form.ref_kind === 'verbal' ? 'e.g. Authorised by Dave Simmons' : form.ref_kind === 'email' ? 'e.g. Email 04/09 — cradle test go-ahead' : 'e.g. PO-4471829'}
                onChange={(e) => setF('reference', e.target.value)} />
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 12 }}>
              <Field label="Ordered by (client)">
                <input value={form.client}
                  onChange={(e) => setF('client', e.target.value)} />
              </Field>
              <Field label="Value authorised (£)" error={errors.value}>
                <input type="number" inputMode="decimal" step="0.01" min="0" value={form.value} placeholder="e.g. 500.00"
                  onChange={(e) => setF('value', e.target.value)} />
              </Field>
            </div>

            {/* Who the invoice actually goes to — pre-resolved from the property. */}
            {parties && (parties.bill_to || parties.options?.length > 0) && (
              <div style={{ background: 'var(--cw-surface-alt)', border: '1px solid var(--cw-border)', borderRadius: 10, padding: '10px 12px' }}>
                <div className="cw-label" style={{ marginBottom: 6 }}>Invoice will be billed to</div>
                <div style={{ fontSize: 'var(--cs-text-base)', fontWeight: 700 }}>
                  {form.bill_to || parties.bill_to || <span style={{ fontWeight: 500, color: 'var(--cw-text-muted)' }}>Not resolved — set the property’s related companies</span>}
                </div>
                {(form.care_of || parties.care_of) && (
                  <div style={{ fontSize: 'var(--cs-text-sm)', color: 'var(--cw-text-2)', marginTop: 2 }}>care of {form.care_of || parties.care_of}</div>
                )}
                {parties.options?.length > 1 && (
                  <Field label="Bill a different party" hint="Changing it here applies to this PO only — the property record is untouched." style={{ marginTop: 8, marginBottom: 0 }}>
                    <select value={form.bill_to || parties.bill_to || ''}
                      onChange={(e) => setF('bill_to', e.target.value)}>
                      {parties.options.map((o) => <option key={o.role} value={o.name}>{o.name}</option>)}
                    </select>
                  </Field>
                )}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 12 }}>
              {!jobId && (
                <Field label="Against which job?">
                  <select value={form.job_id} onChange={(e) => setF('job_id', e.target.value)}>
                    <option value="">Not job-specific</option>
                    {jobOptions.map((j) => (
                      <option key={j.id} value={j.id}>
                        {j.job_number} — {j.title}{j.property_name ? ` · ${j.property_name.trim()}` : ''}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              <Field label="Received on" error={errors.received_on}>
                <input type="date" max={todayLocalISO()} value={form.received_on}
                  onChange={(e) => setF('received_on', e.target.value)} />
              </Field>
            </div>

            <Field label="Notes">
              <textarea style={{ minHeight: 58, resize: 'vertical' }} value={form.notes}
                placeholder="Anything the invoice needs to quote back"
                onChange={(e) => setF('notes', e.target.value)} />
            </Field>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ClientPoPanel;
